import React, { ReactNode } from 'react';
import { useResponsive } from 'common/context';
import DesktopHeader from 'common/components/DesktopHeader';

interface ResponsiveLayoutProps {
  children: ReactNode;
  /** Center content with max-width (for forms, auth pages) */
  centerContent?: boolean;
  /** Custom max-width for centered content */
  maxWidth?: string;
  /** Background color class */
  bgColor?: string;
  /** Show desktop header (default: true on desktop) */
  showHeader?: boolean;
  /** Custom class name for the wrapper */
  className?: string;
}

/**
 * ResponsiveLayout - Wrapper component that handles desktop vs mobile layouts
 *
 * On mobile/native: Renders children unchanged
 * On desktop web: Renders DesktopHeader + expanded main content
 */
export const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  centerContent = false,
  maxWidth = '600px',
  bgColor = 'bg-doqit-primary',
  showHeader = true,
  className = '',
}) => {
  const { shouldExpandForWeb } = useResponsive();

  // Mobile/Native: render children unchanged
  if (!shouldExpandForWeb) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  // Desktop Web: render with header and expanded layout
  return (
    <div className={`min-h-screen ${bgColor}`}>
      {showHeader && <DesktopHeader />}

      {centerContent ? (
        // Centered content (for forms, auth pages, etc.)
        <main className={`flex justify-center py-8 px-4 ${className}`}>
          <div style={{ maxWidth, width: '100%' }}>
            {children}
          </div>
        </main>
      ) : (
        // Full-width content
        <main className={`max-w-desktop-xl mx-auto px-6 py-6 ${className}`}>
          {children}
        </main>
      )}
    </div>
  );
};

export default ResponsiveLayout;
